import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getCategories, getListings, addToFavorites, removeFromFavorites } from '../../services/api';
import { ChevronLeft, Filter, Grid, List, Heart } from 'lucide-react';
import MobileFilterModal from '../../components/layout/MobileFilterModal';
import Loading from '../../components/common/Loading';
import ProductGrid from '../home/ProductGrid';
import DesktopSidebar from '../../components/layout/DesktopSidebar';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import MobileBottomNav from '../../components/layout/MobileBottomNav';
import { AlertModal } from '../../components/common/Modal';
import { useAuth } from '../../context/AuthContext';

const CategoryPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { categoryId } = useParams();
  const { user } = useAuth();
  
  const [category, setCategory] = useState(null);
  const [allCategories, setAllCategories] = useState([]);
  const [allListings, setAllListings] = useState([]);
  const [filteredListings, setFilteredListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [filters, setFilters] = useState({
    sortBy: 'newest',
    condition: 'all',
    location: null,
    minPrice: null,
    maxPrice: null,
    category: null
  });
  
  const [favoritesMap, setFavoritesMap] = useState({});
  const [loadingFavorites, setLoadingFavorites] = useState({});
  const [modal, setModal] = useState({ show: false, message: '', type: 'info' });

  // Calculate user's own listings count
  const userOwnListingsCount = useMemo(() => {
    if (!user) return 0;
    return allListings.filter(listing => listing.seller_id === user.user_id).length;
  }, [allListings, user]);

  // Calculate filtered listings that are NOT user's own
  const filteredNonUserListings = useMemo(() => {
    if (!user) return filteredListings;
    return filteredListings.filter(listing => listing.seller_id !== user.user_id);
  }, [filteredListings, user]);

  useEffect(() => {
    loadAllCategories();
  }, []);

  useEffect(() => {
    if (allCategories.length > 0) {
      determineCategory();
    }
  }, [allCategories, categoryId, location.state]);

  useEffect(() => {
    if (category) {
      loadListings();
    }
  }, [category, filters]);

  const loadAllCategories = async () => {
    try {
      const categoriesData = await getCategories();
      const categories = categoriesData.data || categoriesData || [];
      setAllCategories(categories);
    } catch (err) {
      setError('Failed to load categories');
      setAllCategories([]);
    }
  };

  const determineCategory = () => {
    if (categoryId && categoryId !== 'all') {
      const foundCategory = allCategories.find(cat => cat.id === parseInt(categoryId));
      if (foundCategory) {
        setCategory(foundCategory);
        setFilters(prev => ({ ...prev, category: foundCategory.id }));
      } else {
        setError('Category not found');
        setLoading(false);
      }
    } else if (location.state?.category) {
      setCategory(location.state.category);
      setFilters(prev => ({ ...prev, category: location.state.category.id }));
    } else {
      setCategory({ id: 'all', name: 'All Listings', background_color: '#ff660e', icon_color: '#ffffff' });
    }
  };

  const loadListings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const allListingsData = await getListings({ status: 'active' });
      const allListings = allListingsData.data || allListingsData || [];
      setAllListings(allListings);
      
      // Filter by current category
      let categoryFilteredListings = [...allListings];
      
      if (category?.id && category.id !== 'all') {
        const isMainCategory = category.parent_id === null;
        
        if (isMainCategory) {
          const subcategoryIds = allCategories
            .filter(cat => cat.parent_id === category.id)
            .map(cat => cat.id);
          
          const allCategoryIds = [category.id, ...subcategoryIds];
          
          categoryFilteredListings = categoryFilteredListings.filter(listing => 
            allCategoryIds.includes(parseInt(listing.category_id))
          );
        } else {
          categoryFilteredListings = categoryFilteredListings.filter(listing => 
            parseInt(listing.category_id) === category.id
          );
        }
      }
      
      // Filter out user's own listings
      if (user) {
        categoryFilteredListings = categoryFilteredListings.filter(listing => 
          listing.seller_id !== user.user_id
        );
      }
      
      // Apply additional filters
      let finalFilteredListings = [...categoryFilteredListings];
      
      if (filters.condition && filters.condition !== 'all') {
        finalFilteredListings = finalFilteredListings.filter(
          listing => listing.condition === filters.condition
        );
      }
      
      if (filters.location) {
        finalFilteredListings = finalFilteredListings.filter(
          listing => listing.state_id === filters.location.toString()
        );
      }
      
      if (filters.minPrice) {
        finalFilteredListings = finalFilteredListings.filter(
          listing => parseFloat(listing.price) >= filters.minPrice
        );
      }
      
      if (filters.maxPrice) {
        finalFilteredListings = finalFilteredListings.filter(
          listing => parseFloat(listing.price) <= filters.maxPrice
        );
      }
      
      setFilteredListings(finalFilteredListings);
      
    } catch (err) {
      setError('Failed to load listings');
      setAllListings([]);
      setFilteredListings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFavoriteToggle = useCallback(async (listingId, shouldBeFavorite, listingTitle = 'Item') => {
    if (!user) {
      setModal({ show: true, message: 'Please login to add items to favorites', type: 'warning' });
      return;
    }
  
    setLoadingFavorites(prev => ({ ...prev, [listingId]: true }));
  
    try {
      const currentStatus = favoritesMap[listingId];
      
      setFavoritesMap(prev => ({ ...prev, [listingId]: shouldBeFavorite }));
      
      if (shouldBeFavorite) {
        const response = await addToFavorites(user.user_id, listingId);
        
        if (response.status === 409 || (response.success === false && response.message?.toLowerCase().includes('already'))) {
          setFavoritesMap(prev => ({ ...prev, [listingId]: true }));
          
          setModal({
            show: true,
            message: `"${listingTitle}" is already in your favorites!`,
            type: 'info'
          });
        } 
        else if (!response.success) {
          setFavoritesMap(prev => ({ ...prev, [listingId]: currentStatus || false }));
          
          setModal({
            show: true,
            message: response.message || 'Failed to add to favorites',
            type: 'error'
          });
        }
        else {
          setModal({
            show: true,
            message: `"${listingTitle}" added to favorites!`,
            type: 'success'
          });
        }
      } else {
        const response = await removeFromFavorites(user.user_id, listingId);
        
        if (!response.success) {
          setFavoritesMap(prev => ({ ...prev, [listingId]: currentStatus || true }));
          
          setModal({
            show: true,
            message: response.message || 'Failed to remove from favorites',
            type: 'error'
          });
        } else {
          setModal({
            show: true,
            message: `"${listingTitle}" removed from favorites`,
            type: 'info'
          });
        }
      }
    } catch (error) {
      const currentStatus = favoritesMap[listingId];
      setFavoritesMap(prev => ({ ...prev, [listingId]: currentStatus || false }));
      
      setModal({
        show: true,
        message: 'Network error. Please try again.',
        type: 'error'
      });
    } finally {
      setLoadingFavorites(prev => ({ ...prev, [listingId]: false }));
    }
  }, [user, favoritesMap]);

  // Enhanced listings with favorite data
  const enhancedListings = useMemo(() => {
    return filteredNonUserListings.map(listing => {
      const listingId = listing.listing_id;
      const isLoading = loadingFavorites[listingId] || false;
      
      return {
        ...listing,
        isFavorite: favoritesMap[listingId] || false,
        isLoading: isLoading,
        onFavoriteClick: (listingId, shouldBeFavorite) => 
          handleFavoriteToggle(listingId, shouldBeFavorite, listing.title)
      };
    });
  }, [filteredNonUserListings, favoritesMap, loadingFavorites, handleFavoriteToggle]);

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const clearFilters = () => {
    setFilters({
      sortBy: 'newest',
      condition: 'all',
      location: null,
      minPrice: null,
      maxPrice: null,
      category: category?.id || null
    });
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.condition !== 'all') count++;
    if (filters.location) count++;
    if (filters.minPrice || filters.maxPrice) count++;
    return count;
  };

  const getSubcategories = () => {
    if (!category?.id || category.id === 'all') return [];
    return allCategories.filter(cat => cat.parent_id === category.id);
  };

  const getCategoryIcon = (categoryItem) => {
    if (!categoryItem?.icon_name) return '📦';
    
    const iconMap = {
      'hardware-chip': '💻',
      'phone-portrait': '📱',
      'car': '🚗',
      'shirt': '👕',
      'sparkles': '✨',
      'heart': '❤️',
      'restaurant': '🍴',
      'business': '🏢',
      'home': '🏠',
      'paw': '🐾',
      'construct': '🔧',
      'laptop': '💻',
      'tv': '📺',
      'camera': '📷',
      'game-controller': '🎮',
      'hardware-chip-outline': '💻',
      'print': '🖨️',
      'flash': '⚡',
      'tablet-portrait': '📱',
      'ear': '🎧',
      'watch': '⌚',
      'headset': '🎧',
      'car-sport': '🏎️',
      'bicycle': '🚲',
      'bus': '🚌',
      'build': '🔧',
      'disc': '💿',
      'boat': '🚤',
      'man': '👨',
      'woman': '👩',
      'footsteps': '👟',
      'bag': '👜',
      'diamond': '💎',
      'shirt-outline': '👔',
      'glasses': '👓',
      'basketball': '🏀',
      'water': '💧',
      'cut': '✂️',
      'map': '🗺️',
    };
    
    return iconMap[categoryItem.icon_name] || '📦';
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#f5f5f5]">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-[#fff5e6] rounded-full flex items-center justify-center">
              <span className="text-3xl">⚠️</span>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Error</h3>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => navigate(-1)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
              >
                Go Back
              </button>
              <button
                onClick={() => navigate('/')}
                className="px-4 py-2 bg-[#ff660e] hover:bg-[#e55a0d] text-white rounded-lg transition-colors font-medium"
              >
                Go Home
              </button>
            </div>
          </div>
        </div>
        <Footer />
        <MobileBottomNav />
      </div>
    );
  }

  const currentSubcategories = getSubcategories();
  const activeFilterCount = getActiveFilterCount();

  return (
    <div className="min-h-screen bg-[#f5f5f5]">
      <Header />
      
      <AlertModal
        isOpen={modal.show}
        onClose={() => setModal(prev => ({ ...prev, show: false }))}
        title={
          modal.type === 'success' ? 'Success!' : 
          modal.type === 'warning' ? 'Notice' : 
          modal.type === 'error' ? 'Error' : 'Info'
        }
        message={modal.message}
        type={modal.type}
        buttonText="OK"
        onButtonClick={() => setModal(prev => ({ ...prev, show: false }))}
      />

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-4">
            <button 
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft size={24} />
            </button>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                  style={{ 
                    backgroundColor: category?.background_color || '#ff660e',
                    color: category?.icon_color || '#ffffff'
                  }}
                >
                  {category ? getCategoryIcon(category) : '📦'}
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    {category?.name || 'Category'}
                  </h1>
                  <div className="flex items-center gap-3 text-gray-600 text-sm">
                    <span>
                      {enhancedListings.length} {enhancedListings.length === 1 ? 'listing' : 'listings'} available
                      
                    </span>
                    
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {currentSubcategories.length > 0 && (
            <div className="mb-6">
              <h2 className="text-lg font-semibold mb-4 text-gray-900">Subcategories</h2>
              <div className="flex overflow-x-auto gap-3 pb-2 scrollbar-hide">
                {currentSubcategories.map(subcat => (
                  <button
                    key={subcat.id}
                    onClick={() => navigate(`/category/${subcat.id}`, { state: { category: subcat } })}
                    className="flex-shrink-0 flex flex-col items-center p-4 bg-white hover:bg-[#fff5e6] rounded-xl transition-colors min-w-[120px] group border hover:border-[#ff660e]"
                  >
                    <div 
                      className="w-14 h-14 rounded-full flex items-center justify-center text-2xl mb-3"
                      style={{ backgroundColor: subcat.background_color || '#ff660e' }}
                    >
                      {getCategoryIcon(subcat)}
                    </div>
                    <span className="text-sm font-medium text-gray-900 group-hover:text-[#ff660e] truncate w-full text-center">
                      {subcat.name}
                    </span>
                    <span className="text-xs text-gray-500 mt-1">
                      {allListings.filter(l => parseInt(l.category_id) === subcat.id).length} items
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-6">
          <div className="hidden lg:block w-72 flex-shrink-0">
            <div className="sticky top-24 h-[calc(100vh-96px)]">
              <DesktopSidebar 
                categories={allCategories}
                filters={filters}
                onFilterChange={handleFilterChange}
                listings={enhancedListings}
              />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="mb-6 bg-white rounded-xl shadow-sm p-4 border border-gray-100">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">
                    All Products in {category?.name || 'Category'}
                  </h2>
                  
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="flex border rounded-lg overflow-hidden">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 ${viewMode === 'grid' ? 'bg-[#ff660e] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    >
                      <Grid size={20} />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 ${viewMode === 'list' ? 'bg-[#ff660e] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    >
                      <List size={20} />
                    </button>
                  </div>
                  
                  <button 
                    onClick={() => setShowFilterModal(true)}
                    className="lg:hidden flex items-center gap-2 px-4 py-2 bg-[#ff660e] text-white rounded-lg hover:bg-[#e55a0d] font-medium"
                  >
                    <Filter size={18} />
                    <span>Filter</span>
                    {activeFilterCount > 0 && (
                      <span className="ml-1 bg-white text-[#ff660e] text-xs rounded-full w-5 h-5 flex items-center justify-center">
                        {activeFilterCount}
                      </span>
                    )}
                  </button>

                  <div className="relative">
                    <select
                      value={filters.sortBy}
                      onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value }))}
                      className="pl-4 pr-10 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff660e] appearance-none bg-white text-sm font-medium w-full md:w-auto"
                    >
                      <option value="newest">Newest First</option>
                      <option value="price_low_high">Price: Low to High</option>
                      <option value="price_high_low">Price: High to Low</option>
                      <option value="rating">Highest Rated</option>
                      <option value="views">Most Viewed</option>
                    </select>
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none text-sm">▼</span>
                  </div>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="py-12">
                <Loading 
                  text={`Loading ${category?.name || 'listings'}...`} 
                  size="large" 
                  color="#ff660e" 
                />
              </div>
            ) : enhancedListings.length > 0 ? (
              <ProductGrid 
                listings={enhancedListings}
                viewMode={viewMode}
                onFavoriteClick={(listingId, shouldBeFavorite, listingTitle) => 
                  handleFavoriteToggle(listingId, shouldBeFavorite, listingTitle)
                }
              />
            ) : (
              <div className="text-center py-12 bg-white rounded-xl shadow-sm">
                <div 
                  className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center text-4xl"
                  style={{ 
                    backgroundColor: category?.background_color || '#ff660e',
                    color: category?.icon_color || '#ffffff'
                  }}
                >
                  {getCategoryIcon(category)}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  No listings found
                </h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  {user && userOwnListingsCount > 0 ? (
                    `All listings in this category are your own. Try browsing other categories for items from other sellers.`
                  ) : category?.id === 'all' 
                    ? 'No listings available at the moment. Try adjusting your filters or be the first to create one!'
                    : `No listings found in ${category?.name}. Try adjusting your filters or be the first to create one!`}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button
                    onClick={() => navigate('/')}
                    className="px-6 py-3 bg-white text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors border border-gray-200"
                  >
                    Back to Home
                  </button>
                  {user && userOwnListingsCount > 0 && (
                    <button
                      onClick={() => navigate('/categories')}
                      className="px-6 py-3 bg-[#ff660e] text-white font-medium rounded-lg hover:bg-[#e55a0d] transition-colors"
                    >
                      Browse Other Categories
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
      
      <MobileFilterModal
        isOpen={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        filters={filters}
        onFilterChange={handleFilterChange}
        listingCount={enhancedListings.length}
      />

      <MobileBottomNav />
    </div>
  );
};

export default CategoryPage;